import { Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, Effect, ofType } from '@ngrx/effects'
import { Action, Store, select } from '@ngrx/store'

import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom, filter, take } from 'rxjs/operators'
import { toPayload } from '../../core/helpers/rx-pipes'

import * as checkout from '../actions/checkout'
import * as cart from '../../cart/actions/cart'
import * as orders from '../../orders/actions/orders'
import * as fromCart from '../../cart/reducers'
import * as fromAuth from '../../auth/reducers'

import { CheckoutService } from '../services/checkout-service'
import { DeliveryDateService } from '../services/deliverydate-service'
import { BackorderCountsService } from '../services/backordercount-service'

import { ApiOrder } from '../../cart/models/cart-models'

@Injectable()
export class CheckoutEffects {
    constructor(
        private checkoutSvc: CheckoutService,
        private deliveryDateSvc: DeliveryDateService,
        private backorderCountsSvc: BackorderCountsService,
        private store: Store<fromCart.State>,
        private actions$: Actions,
        private router: Router
    ) { }

    @Effect()
    cleanCart$: Observable<Action> =
        this.actions$.ofType<checkout.SubmitSuccess>(checkout.SUBMIT_SUCCESS)
            .pipe(map(action => new cart.Reset()))

    @Effect()
    setDetails$: Observable<Action> =
        this.actions$.ofType<checkout.Set>(checkout.SET)
        .pipe(
            toPayload<ApiOrder>(),
            switchMap(order => {
              return this.checkoutSvc.setOrderDetails(order)
                    .pipe(
                        map(order => new checkout.SetSuccess(order)),
                        catchError(e => of(new checkout.SetFailed(e)))
                    )
            })
        )

    @Effect()
    order$: Observable<Action> =
      this.actions$
        .pipe(
          ofType<checkout.Submit>(checkout.SUBMIT),
          toPayload<ApiOrder>(),
          switchMap(order => {
            return this.checkoutSvc.submitOrder(order)
              .pipe(
                map(result => new checkout.SubmitSuccess(result)),
                catchError(e => of(new checkout.SubmitFailed(e)))
              )
          })
        )

    @Effect()
    orderCreated$: Observable<Action> =
      this.actions$.ofType<checkout.SubmitSuccess>(checkout.SUBMIT_SUCCESS)
        .pipe(
          toPayload<ApiOrder>(),
          tap(order => this.router.navigate(['/checkout/summary/' + order._id])),
          map(order => new orders.Add(order))
        )

    @Effect()
    getDeliveryDates: Observable<Action> =
      this.actions$.pipe(
        ofType(checkout.GET_DELIVERY_DATES),
        withLatestFrom(
          this.store.pipe(select(fromAuth.getCustomerId)),
          (action, customerId) => ({ action, customerId })
        ),
        switchMap((data) => {
          return this.deliveryDateSvc.getDeliveryDates(data.customerId).pipe(
            map((response) => new checkout.GetDeliveryDatesSuccess(response)),
            catchError((e) => of(new checkout.GetDeliveryDatesFailed(e)))
          )
        })
      )

    @Effect()
    getBackorderCounts$: Observable<Action> = this.actions$.pipe(
      ofType<checkout.GetBackorderCounts>(checkout.GET_BACKORDER_COUNTS),
      withLatestFrom(
        this.store.pipe(select(fromAuth.getCustomerId)),
        this.store.pipe(select(fromCart.getOrderId)),
        (action, customerId, orderId) => ({ action, customerId, orderId })
      ),
      filter((data) => !!data.orderId),
      switchMap((data) => {
        return this.backorderCountsSvc.getBackorderCountsForOrder(data.orderId, data.customerId).pipe(
          map((response) => new checkout.GetBackorderCountsSuccess(response)),
          catchError((e) => of(new checkout.GetBackorderCountsFailed(e)))
        )
      })
    )

  @Effect()
  getBackorderCountsOnFirstSuccesfullCartGet$: Observable<Action> = this.actions$.pipe(
    ofType(cart.GET_SUCCESS),
    take(1),
    withLatestFrom(
      this.store.pipe(select(fromAuth.getCustomerId)),
      this.store.pipe(select(fromCart.getOrderId)),
      (action, customerId, orderId) => ({ action, customerId, orderId })
    ),
    filter((data) => !!data.orderId),
    switchMap((data) => {
      return this.backorderCountsSvc.getBackorderCountsForOrder(data.orderId, data.customerId).pipe(
        map((response) => new checkout.GetBackorderCountsSuccess(response)),
        catchError((e) => of(new checkout.GetBackorderCountsFailed(e)))
      )
    })
  )
}
